import {
  AirQualityMarkerDto,
  AqiStandardId,
  AqiStandardV2Dto,
  DatasourceId,
  NetworkId,
  OpenSiteId,
  Point,
} from '../types';
import { LATITUDE_PRECISION, LONGITUDE_PRECISION, ZOOM_PRECISION } from './constants';
import { openMapAddress } from './openMap';
import { SettingsKey } from './settings-key';
import { Viewport } from './viewport';

export interface MapPageParams {
  datasourceId?: DatasourceId;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  searchedLatitude?: number;
  searchedLongitude?: number;
  aqiStdId?: AqiStandardId;
  disableTour?: boolean;
  openSiteId?: OpenSiteId;
}

const MAP_PAGE_PARAMS_PRECISION: Partial<Record<keyof MapPageParams, number>> = {
  latitude: LATITUDE_PRECISION,
  longitude: LONGITUDE_PRECISION,
  searchedLatitude: LATITUDE_PRECISION,
  searchedLongitude: LONGITUDE_PRECISION,
  zoom: ZOOM_PRECISION,
};

function createQueryString(params: MapPageParams) {
  const qs = (Object.entries(params) as [keyof MapPageParams, unknown][])
    .filter(([, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${key}=${typeof value === 'number' ? value.toFixed(MAP_PAGE_PARAMS_PRECISION[key] ?? 0) : value}`
    )
    .join('&');
  return qs ? `?${qs}` : '';
}

function createMapPageRelativeUrl(networkId: NetworkId, datasourceId: DatasourceId | null, params: MapPageParams) {
  return `${networkId}${datasourceId ? `/ds/${datasourceId}` : ''}${createQueryString(params)}`;
}

export function createShareUrl(networkId: NetworkId, datasourceId: DatasourceId | null, params: MapPageParams) {
  return `${openMapAddress}/api/share/${createMapPageRelativeUrl(networkId, datasourceId, params)}`;
}

export function createMapUrl(networkId: NetworkId, datasourceId: DatasourceId | null, params: MapPageParams) {
  return `${openMapAddress}/${createMapPageRelativeUrl(networkId, datasourceId, params)}`;
}

export function createWidgetCreatorUrl(networkId: NetworkId) {
  return `${openMapAddress}/${networkId}/create-widget`;
}

export function createMapWidgetRelativeUrl(networkId: NetworkId, params: MapPageParams) {
  return `${networkId}/widgets/v1/map${createQueryString(params)}`;
}

export function updateMapUrl(
  networkId: NetworkId,
  viewport: Viewport | null,
  aqiStandard: AqiStandardV2Dto | null,
  searchedLocation: Point | null,
  marker: AirQualityMarkerDto | null,
  openSiteId?: OpenSiteId | null
) {
  const params = new URLSearchParams(window.location.search);
  if (viewport) {
    params.set(SettingsKey.longitude, viewport.longitude.toFixed(MAP_PAGE_PARAMS_PRECISION.longitude));
    params.set(SettingsKey.latitude, viewport.latitude.toFixed(MAP_PAGE_PARAMS_PRECISION.latitude));
    params.set(SettingsKey.zoom, viewport.zoom.toFixed(MAP_PAGE_PARAMS_PRECISION.zoom));
  }
  if (aqiStandard) {
    params.set(SettingsKey.aqiStdId, aqiStandard.aqiStandardId);
  }
  if (searchedLocation) {
    params.set(
      SettingsKey.searchedLongitude,
      searchedLocation.coordinates[0].toFixed(MAP_PAGE_PARAMS_PRECISION.searchedLongitude)
    );
    params.set(
      SettingsKey.searchedLatitude,
      searchedLocation.coordinates[1].toFixed(MAP_PAGE_PARAMS_PRECISION.searchedLatitude)
    );
  } else {
    params.delete(SettingsKey.searchedLongitude);
    params.delete(SettingsKey.searchedLatitude);
  }
  if (openSiteId) {
    params.set(SettingsKey.openSiteId, openSiteId);
  } else {
    params.delete(SettingsKey.openSiteId);
  }
  const path = createMapPageRelativeUrl(networkId, marker?.datasourceId ?? null, {});
  const newUrl = `${path}?${params.toString()}`;
  const originalUrl = window.location.pathname.toString() + window.location.search;
  if (originalUrl !== newUrl) {
    window.history.replaceState({}, '', newUrl);
  }
}
